import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Card } from "@/components/agent/AgentPanels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Target, Plus, Trash2, Download } from "lucide-react";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

const SPEND_FIELDS = [
  { name: "campaign", label: "Campaign (same as utm_campaign / Meta campaign name)", required: true, full: true },
  { name: "platform", label: "Platform", type: "select", options: [{ value: "meta", label: "Meta (Facebook/Instagram)" }, { value: "google", label: "Google" }, { value: "publisher", label: "Publisher / affiliate" }, { value: "other", label: "Other" }] },
  { name: "amount", label: "Amount spent (₹)", type: "number", required: true },
  { name: "period_start", label: "From", type: "date", required: true },
  { name: "period_end", label: "To", type: "date", required: true },
  { name: "reference", label: "Invoice / reference (optional, blocks duplicates)" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
];

const money = (v) => (v == null ? "—" : INR(v));

export default function CampaignCosts() {
  const { hasPerm } = useAuth();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rep, setRep] = useState(null);
  const [spend, setSpend] = useState([]);
  const load = useCallback(() => {
    const params = { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) };
    api.get("/reports/campaign-costs", { params }).then(({ data }) => setRep(data));
    api.get("/campaign-spend").then(({ data }) => setSpend(data));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const canManage = hasPerm("campaign_spend:manage");
  const del = async (s) => {
    if (!window.confirm(`Delete spend ₹${s.amount} for ${s.campaign}?`)) return;
    try { await api.delete(`/campaign-spend/${s.id}`); toast.success("Deleted"); load(); } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const keys = ["campaign", "spend", "leads", "unique_leads", "duplicates", "qualified", "contacted", "docs_completed", "submitted", "cost_per_lead", "cost_per_qualified"];
    const csv = [keys.join(","), ...(rep?.items || []).map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "campaign-costs.csv"; a.click();
  };
  const t = rep?.totals || {};
  const cols = [
    { key: "campaign", header: "Campaign", render: (r) => <div><div className="font-semibold">{r.campaign}</div><div className="text-[11px] text-muted-foreground">{r.sources.join(", ")}</div></div> },
    { key: "spend", header: "Spend", render: (r) => <span className="font-semibold">{r.spend ? INR(r.spend) : "—"}</span> },
    { key: "leads", header: "Leads", render: (r) => <span>{r.leads}<div className="text-[11px] text-muted-foreground">{r.unique_leads} unique · {r.duplicates} dup</div></span> },
    { key: "qualified", header: "Qualified", render: (r) => <span>{r.qualified}<div className="text-[11px] text-muted-foreground">{r.qualified_rate}%</div></span> },
    { key: "contacted", header: "Contacted", render: (r) => <span>{r.contacted}<div className="text-[11px] text-muted-foreground">{r.contact_rate}%</div></span> },
    { key: "docs_completed", header: "Docs done" },
    { key: "submitted", header: "Submitted / converted" },
    { key: "cost_per_lead", header: "Cost / lead", render: (r) => <span className="font-semibold" data-testid="cost-per-lead">{money(r.cost_per_lead)}</span> },
    { key: "cost_per_qualified", header: "Cost / qualified", render: (r) => <span className="font-semibold">{money(r.cost_per_qualified)}</span> },
  ];
  const spendCols = [
    { key: "campaign", header: "Campaign", render: (s) => <span className="font-semibold">{s.campaign}</span> },
    { key: "platform", header: "Platform" },
    { key: "amount", header: "Amount", render: (s) => INR(s.amount) },
    { key: "period", header: "Period", render: (s) => `${fmtDate(s.period_start)} – ${fmtDate(s.period_end)}` },
    { key: "reference", header: "Reference", render: (s) => s.reference || "—" },
    { key: "src", header: "Source", render: (s) => <span className="text-xs">Manual · {s.created_by_name}</span> },
    { key: "a", header: "", render: (s) => canManage && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => del(s)} data-testid="spend-delete-btn"><Trash2 className="h-4 w-4" /></Button> },
  ];
  return (
    <div className="space-y-4">
      <PageHeader title="Campaign Costs" subtitle="Cost per lead and per qualified lead by campaign. Costs only, no revenue or profit" icon={Target}
        actions={canManage && <FormDialog title="Add ad spend" fields={SPEND_FIELDS} initial={{ platform: "meta" }} submitLabel="Save spend" testId="add-spend-dialog"
          onSubmit={async (v) => { await api.post("/campaign-spend", v); load(); }}
          trigger={<Button data-testid="add-spend-btn"><Plus className="h-4 w-4 mr-1.5" />Add spend</Button>} />} />
      <div className="flex flex-wrap gap-3 items-end">
        <div><div className="text-xs text-muted-foreground mb-1">From</div><DatePicker value={from} onChange={setFrom} className="w-48" testId="cc-from" /></div>
        <div><div className="text-xs text-muted-foreground mb-1">To</div><DatePicker value={to} onChange={setTo} className="w-48" testId="cc-to" /></div>
        <Button variant="outline" onClick={exportCsv} data-testid="cc-export-btn"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Total spend", money(t.spend), "cc-total-spend"], ["Leads", t.leads ?? 0, "cc-total-leads"], ["Qualified", t.qualified ?? 0, "cc-total-qualified"], ["Cost / lead", money(t.cost_per_lead), "cc-total-cpl"], ["Cost / qualified", money(t.cost_per_qualified), "cc-total-cpq"]].map(([l, v, id]) => (
          <div key={l} className="rounded-xl border border-border bg-card p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="text-lg font-bold mt-1" data-testid={id}>{v}</div></div>
        ))}
      </div>
      {rep && <p className="text-xs text-muted-foreground" data-testid="cc-note">{rep.note}</p>}
      <DataTable columns={cols} rows={rep?.items || []} loading={!rep} rowTestId="campaign-row" emptyText="No leads in this period" />
      <Card title="Ad spend entries"><DataTable columns={spendCols} rows={spend} rowTestId="spend-row" emptyText="No spend entered yet" /></Card>
    </div>
  );
}
